<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import { EVENT_LANGUAGES, type EventLanguageCode } from '@/types/events'
import { eventSiteUrl } from '@/utils/eventSiteUrl'

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

const { t } = useI18n()
const route = useRoute()
const { event, fetchEvent, updateEvent, lastSavedAt } = useEvents()

const eventSlug = route.params['eventSlug'] as string

onMounted(async () => {
  if (!event.value || event.value.slug !== eventSlug) {
    await fetchEvent(eventSlug)
  }
})

// ── Public site URL ──────────────────────────────────────────────────────────

const publicUrl = computed(() =>
  event.value ? eventSiteUrl(event.value.slug) : null,
)

const copied = ref(false)

async function copyUrl(): Promise<void> {
  if (!publicUrl.value) return
  await navigator.clipboard.writeText(publicUrl.value)
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}

// ── Rebuild status ───────────────────────────────────────────────────────────

type RebuildStatus = 'idle' | 'rebuilding' | 'published'

const rebuildStatus = ref<RebuildStatus>('idle')
let rebuildTimer: ReturnType<typeof setTimeout> | null = null

watch(lastSavedAt, (val) => {
  if (!val) return
  rebuildStatus.value = 'rebuilding'
  if (rebuildTimer) clearTimeout(rebuildTimer)
  rebuildTimer = setTimeout(() => {
    rebuildStatus.value = 'published'
  }, 90_000)
})

onUnmounted(() => {
  if (rebuildTimer) clearTimeout(rebuildTimer)
})

// ── Website enabled toggle ───────────────────────────────────────────────────

const togglingWebsite = ref(false)

async function toggleWebsiteEnabled(): Promise<void> {
  if (!event.value) return
  togglingWebsite.value = true
  try {
    await updateEvent(eventSlug, { name: event.value.name, website_enabled: !event.value.website_enabled })
    // Reset rebuild status so the status indicator reflects the new enabled/disabled
    // state immediately rather than staying on "publishing" for 90 seconds.
    rebuildStatus.value = 'idle'
    if (rebuildTimer) {
      clearTimeout(rebuildTimer)
      rebuildTimer = null
    }
  } finally {
    togglingWebsite.value = false
  }
}

// ── Website language ─────────────────────────────────────────────────────────

const languageForm = ref<EventLanguageCode>('nl')
const languageSaving = ref(false)
const languageError = ref<string | null>(null)
const languageGeneralError = ref<string | null>(null)
const languageSuccess = ref<string | null>(null)

// The panel can mount before fetchEvent() resolves, so prefill both on mount
// (in case the event is already loaded from a previous visit) and reactively
// whenever the event data arrives or changes.
watch(event, (val) => {
  if (val) {
    languageForm.value = val.language
  }
}, { immediate: true })

const languageUnchanged = computed(() => event.value?.language === languageForm.value)

async function saveLanguage(): Promise<void> {
  if (!event.value) return
  languageError.value = null
  languageGeneralError.value = null
  languageSuccess.value = null
  languageSaving.value = true
  try {
    await updateEvent(eventSlug, { name: event.value.name, language: languageForm.value })
    languageSuccess.value = t('events.public_site.language_save_success')
    setTimeout(() => { languageSuccess.value = null }, 4000)
  } catch (e: unknown) {
    const fieldErrors = isAxiosError(e) && e.response.status === 422
      ? e.response.data.errors?.['language']
      : undefined
    if (fieldErrors && fieldErrors.length > 0) {
      languageError.value = fieldErrors[0] ?? null
    } else {
      languageGeneralError.value = t('common.error_generic')
    }
  } finally {
    languageSaving.value = false
  }
}
</script>

<template>
  <div class="space-y-6">

    <!-- Public URL card -->
    <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
      <h2 class="text-base font-semibold text-white mb-1">
        {{ t('events.public_site.heading') }}
      </h2>
      <p class="text-sm text-slate-400 mb-6">
        {{ t('events.public_site.description') }}
      </p>

      <!-- URL row -->
      <div
        v-if="publicUrl"
        class="flex items-center gap-2 bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3"
      >
        <span class="flex-1 min-w-0 truncate text-sm font-mono text-slate-300">
          {{ publicUrl }}
        </span>
        <a
          :href="publicUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="shrink-0 text-slate-400 hover:text-white transition-colors"
          :title="t('events.public_site.open_tooltip')"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
        <button
          type="button"
          class="shrink-0 text-slate-400 hover:text-white transition-colors"
          :title="t('events.public_site.copy_tooltip')"
          @click="copyUrl"
        >
          <svg v-if="!copied" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <svg v-else class="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </button>
      </div>

      <!-- Rebuild status -->
      <div class="mt-4 flex items-center gap-2">
        <template v-if="event && !event.website_enabled">
          <span class="inline-block w-2 h-2 rounded-full bg-slate-500" />
          <span class="text-sm text-slate-400">{{ t('events.public_site.status_offline') }}</span>
        </template>
        <template v-else-if="rebuildStatus === 'rebuilding'">
          <span class="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <span class="text-sm text-amber-400">{{ t('events.public_site.status_publishing') }}</span>
        </template>
        <template v-else-if="rebuildStatus === 'published'">
          <span class="inline-block w-2 h-2 rounded-full bg-emerald-400" />
          <span class="text-sm text-emerald-400">{{ t('events.public_site.status_published') }}</span>
        </template>
        <template v-else-if="event && !event.active_edition">
          <span class="inline-block w-2 h-2 rounded-full bg-slate-500" />
          <span class="text-sm text-slate-400">{{ t('events.public_site.status_holding') }}</span>
        </template>
        <template v-else>
          <span class="inline-block w-2 h-2 rounded-full bg-emerald-400" />
          <span class="text-sm text-emerald-400">{{ t('events.public_site.status_live') }}</span>
        </template>
      </div>

      <!-- View button -->
      <a
        v-if="publicUrl"
        :href="publicUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-6 inline-flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 transition-colors px-5 py-2.5 text-sm font-semibold text-white"
      >
        {{ t('events.public_site.view_button') }}
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>
    </div>

    <!-- Website enabled toggle card -->
    <div
      v-if="event"
      class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8"
    >
      <div class="flex items-start justify-between gap-4">
        <div class="flex-1 min-w-0">
          <h2 class="text-base font-semibold text-white mb-1">
            {{ t('events.public_site.toggle_website_label') }}
          </h2>
          <p class="text-sm text-slate-400">
            {{ t('events.public_site.toggle_website_description') }}
          </p>
        </div>
        <button
          type="button"
          :disabled="togglingWebsite"
          class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
          :class="event.website_enabled ? 'bg-emerald-600' : 'bg-slate-600'"
          :aria-checked="event.website_enabled"
          role="switch"
          @click="toggleWebsiteEnabled"
        >
          <span
            class="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200"
            :class="event.website_enabled ? 'translate-x-6' : 'translate-x-1'"
          />
        </button>
      </div>
    </div>

    <!-- Website language card -->
    <div
      v-if="event"
      class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8"
    >
      <h2 class="text-base font-semibold text-white mb-1">
        {{ t('events.public_site.language_heading') }}
      </h2>
      <p class="text-sm text-slate-400 mb-6">
        {{ t('events.public_site.language_description') }}
      </p>

      <div
        v-if="languageGeneralError"
        class="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
      >
        {{ languageGeneralError }}
      </div>

      <div
        v-if="languageSuccess"
        class="mb-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-sm text-emerald-400"
      >
        {{ languageSuccess }}
      </div>

      <div class="flex flex-col sm:flex-row sm:items-end gap-4">
        <div class="flex-1 max-w-xs">
          <label for="event-website-language" class="block text-xs font-medium text-slate-300 mb-1.5">
            {{ t('events.public_site.language_label') }}
          </label>
          <select
            id="event-website-language"
            v-model="languageForm"
            class="w-full rounded-lg bg-slate-900/60 border border-slate-600 text-slate-200 text-sm px-3 py-2 focus:outline-none focus:border-emerald-500"
            :aria-describedby="languageError ? 'event-website-language-error' : undefined"
          >
            <option v-for="lang in EVENT_LANGUAGES" :key="lang.code" :value="lang.code">
              {{ t(`languages.${lang.code}`) }}
            </option>
          </select>
          <p
            v-if="languageError"
            id="event-website-language-error"
            class="mt-1.5 text-xs text-red-400"
          >
            {{ languageError }}
          </p>
        </div>

        <button
          type="button"
          class="shrink-0 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors text-white text-sm font-semibold"
          :disabled="languageSaving || languageUnchanged"
          @click="saveLanguage"
        >
          <span v-if="!languageSaving">{{ t('common.save') }}</span>
          <span v-else>{{ t('common.saving') }}</span>
        </button>
      </div>
    </div>

  </div>
</template>
